import { useEffect, useRef, useState } from "react";
import { Check, ChevronDown, Eye, FileDiff, PencilLine } from "lucide-react";
import { cn } from "@/lib/utils";

// Google Docs-style mode switch in the header: Editing (direct edits),
// Suggesting (Syncfusion track changes) and Viewing (read-only). Viewers of
// a shared Drive file are locked to Viewing.

export type DocMode = "editing" | "suggesting" | "viewing";

const MODES = [
    { id: "editing", label: "Editing", description: "Edit the document directly", Icon: PencilLine },
    { id: "suggesting", label: "Suggesting", description: "Edits become suggestions to accept or reject", Icon: FileDiff },
    { id: "viewing", label: "Viewing", description: "Read or print the final document", Icon: Eye }
] as const;

export function DocModeMenu({ mode, onChange, locked }: {
    mode: DocMode;
    onChange: (mode: DocMode) => void;
    /** View-only access — the switch just shows "Viewing". */
    locked: boolean;
}) {
    const [open, setOpen] = useState(false);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const current = MODES.find((m) => m.id === mode) ?? MODES[0];
    // A `.suite-appbar-pill` on the suite top bar (Accent, Light or Dark);
    // Suggesting reads a shade stronger so the mode is visible at a glance.

    useEffect(() => {
        if (!open) return;
        const items = Array.from(menuRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitemradio"]') ?? []);
        (items.find((i) => i.getAttribute("aria-checked") === "true") ?? items[0])?.focus();
        const onPointer = (e: PointerEvent) => {
            const t = e.target as Node;
            if (!menuRef.current?.contains(t) && !buttonRef.current?.contains(t)) setOpen(false);
        };
        document.addEventListener("pointerdown", onPointer, true);
        return () => document.removeEventListener("pointerdown", onPointer, true);
    }, [open]);

    if (locked) {
        return (
            <span
                className="suite-appbar-pill"
                title="You have view-only access to this file"
            >
                <Eye className="h-3.5 w-3.5" aria-hidden />
                <span className="hidden md:inline">Viewing</span>
                <span className="sr-only">(view-only access)</span>
            </span>
        );
    }

    const close = (refocus: boolean) => {
        setOpen(false);
        if (refocus) buttonRef.current?.focus();
    };
    const pick = (next: DocMode) => {
        close(true);
        if (next !== mode) onChange(next);
    };
    const onMenuKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        const items = Array.from(menuRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitemradio"]') ?? []);
        const at = items.indexOf(document.activeElement as HTMLButtonElement);
        let target: HTMLButtonElement | undefined;
        if (e.key === "ArrowDown") target = items[(at + 1) % items.length];
        else if (e.key === "ArrowUp") target = items[(at - 1 + items.length) % items.length];
        else if (e.key === "Home") target = items[0];
        else if (e.key === "End") target = items[items.length - 1];
        else if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); close(true); return; }
        else if (e.key === "Tab") { setOpen(false); return; }
        if (target) { e.preventDefault(); target.focus(); }
    };

    return (
        <div className="relative">
            <button
                ref={buttonRef}
                type="button"
                aria-haspopup="menu"
                aria-expanded={open}
                aria-label={`Mode: ${current.label}`}
                title={`${current.label} — ${current.description}`}
                onClick={() => setOpen((v) => !v)}
                onKeyDown={(e) => { if (e.key === "ArrowDown") { e.preventDefault(); setOpen(true); } }}
                className={cn(
                    "suite-appbar-pill",
                    mode === "suggesting" && "bg-[color-mix(in_srgb,var(--appbar-fg)_30%,transparent)] hover:bg-[color-mix(in_srgb,var(--appbar-fg)_36%,transparent)]"
                )}
            >
                <current.Icon className="h-3.5 w-3.5" aria-hidden />
                <span className="hidden md:inline">{current.label}</span>
                <ChevronDown className="h-3.5 w-3.5 opacity-80" aria-hidden />
            </button>
            {open ? (
                <div
                    ref={menuRef}
                    role="menu"
                    aria-label="Document mode"
                    onKeyDown={onMenuKeyDown}
                    className="absolute right-0 top-full z-[70] mt-1 w-72 rounded-md border bg-popover p-1 text-popover-foreground shadow-lg"
                >
                    {MODES.map((m) => {
                        const checked = m.id === mode;
                        return (
                            <button
                                key={m.id}
                                type="button"
                                role="menuitemradio"
                                aria-checked={checked}
                                onClick={() => pick(m.id)}
                                className="flex w-full items-start gap-3 rounded-sm px-2 py-2 text-left hover:bg-accent focus-visible:bg-accent focus-visible:outline-none"
                            >
                                <m.Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                                <span className="min-w-0 flex-1">
                                    <span className="block text-sm font-medium">{m.label}</span>
                                    <span className="block text-xs text-muted-foreground">{m.description}</span>
                                </span>
                                {checked ? <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden /> : null}
                            </button>
                        );
                    })}
                </div>
            ) : null}
        </div>
    );
}
